// src/shared/components/SystemLogsViewer.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface LogEntry {
  timestamp: string;
  level: 'log' | 'warn' | 'error' | 'info';
  message: string;
  stack?: string;
}

const SystemLogsViewer: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'error' | 'warn' | 'log' | 'info'>('all');

  useEffect(() => {
    const updateLogs = () => {
      try {
        if ((window as any).systemLogs && Array.isArray((window as any).systemLogs)) {
          setLogs([...(window as any).systemLogs]);
        }
      } catch (error) {
        console.error('Error updating logs:', error);
      }
    };

    // Update logs every second
    const interval = setInterval(updateLogs, 1000);
    updateLogs();

    return () => clearInterval(interval);
  }, []);

  const filteredLogs = filter === 'all' 
    ? logs 
    : logs.filter(log => log.level === filter);

  const getLogColor = (level: string) => {
    switch (level) {
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      case 'warn': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'info': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const exportLogs = () => {
    try {
      const logText = logs.map(log => 
        `[${log.timestamp}] ${log.level.toUpperCase()}: ${log.message}`
      ).join('\n');
      
      const blob = new Blob([logText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `system-logs-${new Date().toISOString()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting logs:', error);
      alert('Failed to export logs. Please check the console.');
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-50 bg-blue-600 text-white text-sm px-3 py-2 rounded-lg shadow-lg hover:bg-blue-700 transition-colors"
        title="View System Logs"
      >
        📋 Logs ({logs.length})
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed bottom-4 left-4 z-50 w-96 h-96 bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col"
    >
      <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-800">System Logs ({logs.length})</h3>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="all">All</option>
            <option value="error">Errors</option>
            <option value="warn">Warnings</option>
            <option value="log">Logs</option>
            <option value="info">Info</option>
          </select>
          <button
            onClick={exportLogs}
            className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
          >
            Export
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700"
          >
            ✕
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {filteredLogs.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-8">No logs to display</div>
        ) : (
          filteredLogs.slice().reverse().map((log, index) => (
            <div
              key={index}
              className={`text-xs p-2 rounded border ${getLogColor(log.level)}`}
            >
              <div className="font-mono text-[10px] text-gray-500 mb-1">
                {new Date(log.timestamp).toLocaleTimeString()}
              </div>
              <div className="break-words">{log.message}</div>
              {log.stack && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-gray-500">Stack trace</summary>
                  <pre className="text-[10px] mt-1 overflow-x-auto">{log.stack}</pre>
                </details>
              )}
            </div>
          ))
        )}
      </div>
      
      <div className="p-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-600">
        Errors: {logs.filter(l => l.level === 'error').length} | 
        Warnings: {logs.filter(l => l.level === 'warn').length}
      </div>
    </motion.div>
  );
};

export default SystemLogsViewer;

